import React from 'react'
import { Box, Dialog, IconButton, Typography, styled } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Button, Modal } from '@/design-system'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  danger?: boolean
  loading?: boolean
  onConfirm: () => void
  onClose: () => void
  /**
   * Stack above immersive surfaces. Album Lightbox sits at 1400 — pass
   * something higher (e.g. 1600) when confirming a delete from inside it,
   * otherwise the dialog opens invisibly underneath.
   */
  zIndex?: number
  /**
   * `night` — dark glass over immersive surfaces (lightbox).
   * `light` — design-system Modal (album page, connections-style confirms).
   */
  variant?: 'light' | 'night'
}

const NightPaper = styled(Box)({
  width: '100%',
  maxWidth: 400,
  borderRadius: 18,
  padding: '26px 28px 22px',
  background: 'rgba(23,22,29,0.98)',
  border: '1px solid rgba(244,243,246,0.12)',
  boxShadow: '0 28px 80px rgba(0,0,0,0.55)',
  color: 'var(--ds-night-ink, #f4f3f6)',
})

const NightHead = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 10,
})

const NightTitle = styled(Typography)({
  margin: 0,
  fontSize: 18,
  fontWeight: 720,
  letterSpacing: '-0.02em',
  color: 'var(--ds-night-ink, #f4f3f6)',
})

const NightCopy = styled(Typography)({
  margin: '0 0 22px',
  fontSize: 13.5,
  lineHeight: 1.6,
  color: 'var(--ds-night-ink-muted, rgba(244,243,246,0.6))',
})

const Footer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
})

const NightGhost = styled(Button)({
  color: 'var(--ds-night-ink-muted, rgba(244,243,246,0.6)) !important',
  background: 'transparent !important',
  '&:hover': {
    color: 'var(--ds-night-ink, #f4f3f6) !important',
    background: 'rgba(244,243,246,0.06) !important',
  },
})

/** Coral destroy CTA — matches settings ConfirmDangerModal / mock `.btn-danger`. */
const NightDanger = styled(Button)({
  backgroundColor: 'var(--ds-accent-deep-a, #e97a4a) !important',
  color: '#fff !important',
  '&:hover': {
    backgroundColor: 'var(--ds-accent-deep-a, #e97a4a) !important',
    opacity: 0.88,
  },
  '&.Mui-disabled': {
    backgroundColor: 'var(--ds-accent-deep-a, #e97a4a) !important',
    color: '#fff !important',
  },
})

const NightClose = styled(IconButton)({
  color: 'rgba(244,243,246,0.45)',
  margin: '-6px -8px 0 0',
  '&:hover': { color: 'rgba(244,243,246,0.9)', background: 'rgba(244,243,246,0.08)' },
})

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Delete',
  cancelLabel = 'Cancel',
  danger = true,
  loading,
  onConfirm,
  onClose,
  zIndex,
  variant = 'light',
}: ConfirmDialogProps) {
  const handleClose = loading ? () => undefined : onClose

  if (variant === 'night') {
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        sx={zIndex != null ? { zIndex } : undefined}
        PaperProps={{
          sx: {
            background: 'transparent',
            boxShadow: 'none',
            margin: 2,
            maxWidth: 420,
            width: '100%',
            overflow: 'visible',
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(6,5,10,0.55)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
            },
          },
        }}
      >
        <NightPaper>
          <NightHead>
            <NightTitle>{title}</NightTitle>
            <NightClose aria-label='Close' size='small' onClick={handleClose} disabled={loading}>
              <CloseIcon fontSize='small' />
            </NightClose>
          </NightHead>
          {description && <NightCopy>{description}</NightCopy>}
          <Footer>
            <NightGhost variant='ghost' size='sm' onClick={handleClose} disabled={loading}>
              {cancelLabel}
            </NightGhost>
            {danger ? (
              <NightDanger variant='danger' size='sm' onClick={onConfirm} loading={loading}>
                {confirmLabel}
              </NightDanger>
            ) : (
              <Button variant='primary' size='sm' onClick={onConfirm} loading={loading}>
                {confirmLabel}
              </Button>
            )}
          </Footer>
        </NightPaper>
      </Dialog>
    )
  }

  return (
    <Modal open={open} onClose={handleClose} title={title} sx={zIndex != null ? { zIndex } : undefined}>
      {description && (
        <Typography sx={{ fontSize: 13.5, color: 'var(--ds-ink-muted)', lineHeight: 1.6 }}>
          {description}
        </Typography>
      )}
      <Footer sx={{ marginTop: 'var(--ds-space-4)' }}>
        <Button variant='ghost' onClick={handleClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
          {confirmLabel}
        </Button>
      </Footer>
    </Modal>
  )
}
